// This is a COPY of downread/shared/src/schema/site-css.ts. To modify, edit the original and then run downread/bin/build-shared.sh
// Per-site stylesheets (sync-server/site-css/): the naming rules shared by the sync server, which
// keys and publishes the stylesheets, and the client, which loads them and tags post cards.
// Keep this file free of imports so build-shared.sh can copy it to frontend/src/schema-copy.

/**
 * The domain a site stylesheet is keyed by: the URL's host, lowercased, without a leading `www.`.
 * Undefined when the URL has no host (unparsable, or a newsletter's mailto: URL).
 */
export function siteDomain(url: string | undefined): string | undefined {
    if (!url) return undefined;
    try {
        return new URL(url).hostname.toLowerCase().replace(/^www\./, '') || undefined;
    } catch {
        return undefined;
    }
}

/**
 * The class that marks a post card as belonging to a site, and that every rule of the site's
 * stylesheet is prefixed with: `feed-<domain>` with dots turned into underscores, so a stylesheet
 * writes `.feed-nytimes_com` instead of the escaped `.feed-nytimes\.com`. Underscores can't occur
 * in a hostname, so the mapping is unambiguous.
 */
export const siteCssClass = (domain: string): string => `feed-${domain.replace(/\./g, '_')}`;

/**
 * CDN path of a site stylesheet, relative to the CDN base URL. The content hash is part of the
 * name, so every object is immutable and cacheable forever; a changed stylesheet gets a new path,
 * and feeds pick it up (Feed.siteCss) on their next fetch.
 */
export const siteCssPath = (domain: string, hash: string): string => `site-css/${domain}.${hash}.css`;

/** Two-label public suffixes (eTLD) where the registrable domain is the last THREE labels.
 *  Pragmatic subset of the public suffix list — enough for real-world newsletter senders without
 *  pulling in a multi-megabyte PSL dependency that the frontend bundle would also carry. */
const MULTI_LABEL_SUFFIXES = new Set([
    'co.uk', 'org.uk', 'gov.uk', 'ac.uk', 'me.uk', 'ltd.uk', 'plc.uk', 'net.uk', 'sch.uk',
    'com.au', 'net.au', 'org.au', 'edu.au', 'gov.au', 'co.nz', 'org.nz', 'net.nz',
    'co.jp', 'or.jp', 'ne.jp', 'co.kr', 'or.kr', 'co.za', 'org.za', 'co.in', 'net.in', 'org.in',
    'com.br', 'com.mx', 'com.ar', 'com.tr', 'co.il', 'com.sg', 'com.hk', 'com.cn',
]);

/** Reduce a host to its registrable domain (eTLD+1). `mail.nytimes.com` -> `nytimes.com`,
 *  `news.example.co.uk` -> `example.co.uk`. Hosts with two or fewer labels are returned as-is. */
export function registrableDomain(host: string): string {
    const labels = host.toLowerCase().replace(/\.+$/, '').split('.').filter(Boolean);
    if (labels.length <= 2) return labels.join('.');
    const lastTwo = labels.slice(-2).join('.');
    const take = MULTI_LABEL_SUFFIXES.has(lastTwo) ? 3 : 2;
    return labels.slice(-take).join('.');
}
